<template>
  <div>
    <h1 class="title">{{ $t('general.your-data') }}</h1>
    <div class="box">
      <ValidationObserver v-slot="{invalid}" v-if="userdata">
        <b-notification type="is-success" v-if="saved" :closable="false">
          {{ $t('notices.data-has-been-refreshed') }}
        </b-notification>
        <b-notification type="is-danger" v-if="error" :closable="false">
          <span v-if="errors.length == 0">{{ $t('errors.retryable-error') }}</span>
          <ul v-if="errors.length > 0">
            <li v-for="(e, i) in registerErrors" :key="i">{{ e.value }}</li>
          </ul>
        </b-notification>
        <h2 class="title is-size-5">{{ $t('general.contact-data') }}</h2>
        <div class="block">
          <ValidationProvider :name="`${$t('general.male')}/${$t('general.female')}`" rules="oneOf:10,20" v-slot="{errors}">
            <b-radio id="radioMale" v-model="userdata.sex" name="sex" native-value="10">{{ $t('general.male') }}</b-radio>
            <b-radio id="radioFemale" v-model="userdata.sex" name="sex" native-value="20">{{ $t('general.female') }}</b-radio>
            <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
          </ValidationProvider>
        </div>
        <div class="columns is-multiline">
          <div class="column is-12-tablet is-5-widescreen">
            <ValidationProvider name="Vorname" :rules="{required: true}" v-slot="{errors}">
              <b-field :label="`${$t('general.firstname')}*`" expanded label-position="inside">
                <b-input name="firstName" v-model="userdata.firstName"></b-input>
                <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
          <div class="column is-12-tablet is-7-widescreen">
            <ValidationProvider name="Nachname" :rules="{required: true}" v-slot="{errors}">
              <b-field :label="`${$t('general.lastname')}*`" expanded label-position="inside">
                <b-input name="lastName" v-model="userdata.lastName"></b-input>
                <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
        </div>
        <Separator />
        <h2 class="title is-size-5">{{ $t('general.phone-email-data') }}</h2>
        <div class="columns is-multiline">
          <div class="column is-12-tablet is-5-widescreen">
            <b-field :label="$t('general.phone')" expanded label-position="inside">
              <b-input v-model="userdata.phoneNumber" inputmode="tel"></b-input>
            </b-field>
          </div>
          <div class="column is-12-tablet is-7-widescreen">
            <b-field :label="$t('general.email')" expanded label-position="inside">
              <b-input v-model="userdata.email" inputmode="email"></b-input>
            </b-field>
          </div>
        </div>
        <Separator />
        <h2 class="title is-size-5">{{ $t('general.address-data') }}</h2>
        <div class="columns is-multiline">
          <div class="column is-7 is-12-desktop is-7-widescreen">
            <ValidationProvider name="Adresse" :rules="{required: true}" v-slot="{errors}">
              <b-field :label="`${$t('general.street')} + ${$t('general.number')}*`" label-position="inside">
                <b-input name="streetInput" v-model="userdata.address"></b-input>
                <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
          <div class="column is-5 is-12-desktop is-5-widescreen">
            <ValidationProvider name="Land" :rules="{required: true}" v-slot="{errors}">
              <b-field :label="`${$t('general.country')}*`" label-position="inside">
                <b-select placeholder="Land" v-model="userdata.country" icon="globe" expanded>
                  <option v-for="option in isoCountries" :value="option.iso" :key="option.iso">{{ option.country }}</option>
                </b-select>
                <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
          <div class="column is-4">
            <ValidationProvider name="PLZ" :rules="{required: true, numeric: true}" v-slot="{errors}">
              <b-field :label="`${$t('general.zip')}*`" label-position="inside">
                <b-input name="plz" v-model="userdata.zip" inputmode="numeric"></b-input>
                <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
          <div class="column is-8">
            <ValidationProvider name="Stadt" :rules="{required: true}" v-slot="{errors}">
              <b-field :label="`${$t('general.city')}*`" label-position="inside">
                <b-input name="city" v-model="userdata.city"></b-input>
                <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
        </div>
        <p class="mb-4">
          <small>(* {{ $t('general.required-field') }})</small>
        </p>
        <div class="is-sticky-mobile--bottom u-has-background-white-o75  u-p1--mobile">
          <b-button type="is-primary" size="is-medium" class="is-fullwidth--desktop" :disabled="invalid" @click="change(userdata)" expanded>{{ $t('general.daten-aktualisieren') }}</b-button>
        </div>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import Separator from '@/components/Separator'

export default {
  name: 'UserChangeData',
  components: {
    Separator,
  },
  computed: {
    ...mapGetters({
      userdata: '$_account/userdata',
      isoCountries: 'isoCountries',
    }),
  },
  data() {
    return {
      saved: false,
      error: false,
      errors: [],
    }
  },
  async mounted() {
    await this.$store.dispatch('$_account/getUserData')
  },
  methods: {
    async change(data) {
      this.saved = false
      this.errors = []
      this.error = false
      const res = await this.$store.dispatch('$_account/updateUserData', data)
      if (!res.success) {
        this.error = true
      } else {
        this.saved = true
      }
    },
  },
}
</script>

<style scoped lang="scss"></style>
